import PrivateRoute from 'js/components/route_utils/PrivateRoute';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import {
  ROUTE_AI,
  ROUTE_AI_TOOLS,
  ROUTE_AI_TOOL_SETS,
  ROUTE_AI_MCP_FLOWS,
} from 'new/constants/pathnames';

import { ScreenComponent } from 'new/routes/RouterManager';
import { configureScreenHOC } from 'new/routes/configureScreenHOC';
import Drawer, { DrawerItemProps } from 'new/components/drawer/Drawer';
import { router } from 'new/routes/router';
import React, { useEffect, useMemo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import structure, { StructureGroup, StructureItem } from 'new/routes/structure';
import { useSectionNavigation, useLastVisitedPath } from 'new/hooks/useSectionNavigation';
import { ToolsetsSection } from './sections/ToolsAndToolsetsSection';
import { ToolsManagementSection } from './sections/ToolsManagementSection';
import { MCPFlowsScreen } from './screens/MCPFlowsScreen';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { aiApi } from './state/aiApi';
import { getFeatureFlagValue } from 'new/constants/featureFlags';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';

const MCPFlowsRoute = configureScreenHOC(MCPFlowsScreen);

type DrawerContentGroup = {
  name?: string;
  badge?: string;
  items: DrawerItemProps[];
};

const mapStructureItemToDrawerItem = (item: StructureItem): DrawerItemProps => ({
  title: typeof item.title === 'string' ? item.title : String(item.title),
  to: item.to,
  onClick: item.onClick,
  Icon: item.Icon,
  BaseComponent: item.BaseComponent,
  badge: item.badge,
  isCollapsed: item.isCollapsed,
  isDisabled: item.isDisabled,
});

const mapStructureGroupToDrawerContent = (group: StructureGroup): DrawerContentGroup => ({
  name: group.name,
  badge: group.badge,
  items: group.items
    .filter((item) => {
      if (!item.featureFlagKey) return true;
      return getFeatureFlagValue(item.featureFlagKey);
    })
    .map(mapStructureItemToDrawerItem),
});

export const AI: ScreenComponent<Record<string, never>> = () => {
  useSectionNavigation('ai');

  const dispatch = useAppDispatch();
  const orgId = useCurrentOrg()?.id;
  const isMcpFlowsEnabled = useFeatureFlag('UI_ENABLE_MCP_FLOWS');

  // Reset the aiApi cache whenever orgId changes (including org switch).
  // Placed here rather than in each tab section so tab navigation between
  // Tools and Toolsets does not trigger unnecessary resets.
  useEffect(() => {
    if (orgId == null) return;
    dispatch(aiApi.util.resetApiState());
  }, [orgId, dispatch]);

  const defaultPath = `${ROUTE_REDESIGN}${ROUTE_AI_TOOL_SETS}`;
  const lastVisitedPath = useLastVisitedPath('ai', ROUTE_AI, defaultPath);

  const mappedGroups = useMemo(() => {
    const groups = structure.find((s) => s.name === 'AI')?.groups?.() || [];
    return groups.map(mapStructureGroupToDrawerContent);
  }, []);

  return (
    <Drawer content={mappedGroups}>
      <Switch>
        <PrivateRoute
          exact
          path={`${ROUTE_REDESIGN}${ROUTE_AI_TOOLS}`}
          component={ToolsManagementSection}
        />
        <PrivateRoute
          exact
          path={`${ROUTE_REDESIGN}${ROUTE_AI_TOOL_SETS}`}
          component={ToolsetsSection}
        />
        {isMcpFlowsEnabled && (
          <PrivateRoute
            exact
            path={`${ROUTE_REDESIGN}${ROUTE_AI_MCP_FLOWS}`}
            component={MCPFlowsRoute}
          />
        )}
        {router.manager.getRoutes('ai').map((route) => (
          <PrivateRoute key={route.name} {...route} />
        ))}
        <Route path="*">
          {lastVisitedPath ? <Redirect to={lastVisitedPath} /> : <Redirect to={defaultPath} />}
        </Route>
      </Switch>
    </Drawer>
  );
};

AI.displayName = 'AI';

AI.screenConfig = {
  path: `${ROUTE_REDESIGN}${ROUTE_AI}`,
  domain: 'main',
  screenName: 'AI',
  header: {
    title: 'AI',
  },
};
